import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { defaultContent } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentContext = createContext(null);

const LS_TOKEN = "yesonh_token";
const MAX_HISTORY = 100;

export function ContentProvider({ children }) {
  // Content is derived from a history stack so we can undo/redo.
  // hist = { stack: [content, ...], index }
  const [hist, setHist] = useState({ stack: [defaultContent], index: 0 });
  const content = hist.stack[hist.index];

  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [token, setToken] = useState(
    () => localStorage.getItem(LS_TOKEN) || ""
  );
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const isAdmin = !!token;
  const canUndo = hist.index > 0;
  const canRedo = hist.index < hist.stack.length - 1;

  // Drop an invalid/expired token but keep the current (possibly unsaved) content
  const expireSession = useCallback(() => {
    localStorage.removeItem(LS_TOKEN);
    setToken("");
    setSessionExpired(true);
  }, []);

  const isAuthError = (e) => e?.response?.status === 401 || e?.response?.status === 403;

  // Validate a stored token on load
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch((e) => {
        if (isAuthError(e)) expireSession();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replace history with a fresh baseline (load / reset)
  const initHistory = useCallback((newContent) => {
    setHist({ stack: [newContent], index: 0 });
    setDirty(false);
  }, []);

  // Load content from backend on mount
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/content`);
        if (active && res.data && res.data.sections) {
          initHistory(res.data);
        }
      } catch (e) {
        console.warn("Using default content:", e?.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [initHistory]);

  // Apply theme colors as CSS variables
  useEffect(() => {
    const root = document.documentElement;
    if (content?.theme) {
      root.style.setProperty("--navy", content.theme.navy);
      root.style.setProperty("--red", content.theme.red);
      root.style.setProperty("--lightblue", content.theme.lightblue);
    }
  }, [content?.theme]);

  const login = useCallback(async (password) => {
    const res = await axios.post(`${API}/auth/login`, { password });
    const t = res.data.token;
    localStorage.setItem(LS_TOKEN, t);
    setToken(t);
    setSessionExpired(false);
    return t;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(LS_TOKEN);
    setToken("");
    setEditMode(false);
    setSessionExpired(false);
  }, []);

  // ---- Commit a new content state onto the history stack ----
  const commit = useCallback((updater) => {
    setHist((prev) => {
      const current = prev.stack[prev.index];
      const next =
        typeof updater === "function" ? updater(current) : updater;
      let stack = prev.stack.slice(0, prev.index + 1);
      stack.push(next);
      if (stack.length > MAX_HISTORY) {
        stack = stack.slice(stack.length - MAX_HISTORY);
      }
      return { stack, index: stack.length - 1 };
    });
    setDirty(true);
  }, []);

  const updateContent = commit;

  const undo = useCallback(() => {
    setHist((prev) =>
      prev.index > 0 ? { ...prev, index: prev.index - 1 } : prev
    );
    setDirty(true);
  }, []);

  const redo = useCallback(() => {
    setHist((prev) =>
      prev.index < prev.stack.length - 1
        ? { ...prev, index: prev.index + 1 }
        : prev
    );
    setDirty(true);
  }, []);

  const updateHeader = useCallback(
    (patch) =>
      commit((prev) => ({ ...prev, header: { ...prev.header, ...patch } })),
    [commit]
  );

  const updateTheme = useCallback(
    (patch) =>
      commit((prev) => ({ ...prev, theme: { ...prev.theme, ...patch } })),
    [commit]
  );

  const updateSection = useCallback(
    (id, patch) =>
      commit((prev) => ({
        ...prev,
        sections: prev.sections.map((s) =>
          s.id === id
            ? { ...s, ...(typeof patch === "function" ? patch(s) : patch) }
            : s
        ),
      })),
    [commit]
  );

  const moveSection = useCallback(
    (id, dir) =>
      commit((prev) => {
        const idx = prev.sections.findIndex((s) => s.id === id);
        if (idx < 0) return prev;
        const target = idx + dir;
        if (target < 0 || target >= prev.sections.length) return prev;
        const sections = [...prev.sections];
        const [item] = sections.splice(idx, 1);
        sections.splice(target, 0, item);
        return { ...prev, sections };
      }),
    [commit]
  );

  const removeSection = useCallback(
    (id) =>
      commit((prev) => ({
        ...prev,
        sections: prev.sections.filter((s) => s.id !== id),
      })),
    [commit]
  );

  const addSection = useCallback(
    (section, afterId) =>
      commit((prev) => {
        const sections = [...prev.sections];
        if (!afterId) {
          sections.push(section);
        } else {
          const idx = sections.findIndex((s) => s.id === afterId);
          sections.splice(idx + 1, 0, section);
        }
        return { ...prev, sections };
      }),
    [commit]
  );

  const save = useCallback(
    async (authToken = token) => {
      setSaving(true);
      try {
        await axios.put(`${API}/content`, content, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setDirty(false);
        return true;
      } catch (e) {
        if (isAuthError(e)) expireSession();
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [content, token, expireSession]
  );

  const resetContent = useCallback(async () => {
    try {
      const res = await axios.post(
        `${API}/content/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      initHistory(res.data);
    } catch (e) {
      if (isAuthError(e)) expireSession();
      throw e;
    }
  }, [token, initHistory, expireSession]);

  // Keyboard shortcuts for undo/redo (ignored while typing in an editable field)
  useEffect(() => {
    if (!editMode || !isAdmin) return;
    const handler = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const el = document.activeElement;
      const typing =
        el &&
        (el.isContentEditable ||
          el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA");
      if (typing) return; // let the browser handle native text undo
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editMode, isAdmin, undo, redo]);

  const value = useMemo(
    () => ({
      content,
      loading,
      editMode,
      setEditMode,
      isAdmin,
      token,
      dirty,
      saving,
      sessionExpired,
      canUndo,
      canRedo,
      undo,
      redo,
      login,
      logout,
      updateContent,
      updateHeader,
      updateTheme,
      updateSection,
      moveSection,
      removeSection,
      addSection,
      save,
      resetContent,
    }),
    [
      content,
      loading,
      editMode,
      isAdmin,
      token,
      dirty,
      saving,
      sessionExpired,
      canUndo,
      canRedo,
      undo,
      redo,
      login,
      logout,
      updateContent,
      updateHeader,
      updateTheme,
      updateSection,
      moveSection,
      removeSection,
      addSection,
      save,
      resetContent,
    ]
  );

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
}
